import { MdKeyboardArrowDown } from 'react-icons/md';
import { AnimatePresence, motion } from 'motion/react'
import React, { useEffect, useState } from 'react';
import { Styled } from './';
import { StandardOrderDetailsProps } from 'components/ITeroProposal/ITeroProposal.types';
import { useI18n } from 'contexts/i18n';

const StandardOrderDetails = ({
  currentProposal,
  isServiceTermHideRequired,
}: StandardOrderDetailsProps) => {
  const i18n = useI18n();
  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    const initialExpandedItems = currentProposal.lineitems.reduce(
      (acc, item) => {
        acc[item.name] = true;
        return acc;
      },
      {} as { [key: string]: boolean }
    );
    setExpandedItems(initialExpandedItems);
  }, [currentProposal.lineitems]);

  const toggleExpand = (itemName: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const BodyCellOptionsAnimationProps = {
    as: motion.div,
    initial: { height: 0, opacity: 0 },
    animate: { height: 'auto', opacity: 1 },
    exit: { height: 0, opacity: 0 },
    transition: { duration: 0.1 },
    style: { overflow: 'hidden' },
  };

  return (
    <Styled.Proposal>
      <Styled.Head>
        <Styled.HeaderCell>{i18n['Product']}</Styled.HeaderCell>
        <Styled.HeaderCell>{i18n['Quantity']}</Styled.HeaderCell>
        <Styled.HeaderCell>{i18n['List Price']}</Styled.HeaderCell>
        <Styled.HeaderCell>{i18n['Promotions/Discounts']}</Styled.HeaderCell>
        <Styled.HeaderCell>{i18n['Total Price']}</Styled.HeaderCell>
      </Styled.Head>
      <Styled.BodyRows>
        {currentProposal.lineitems.map(item => (
          <React.Fragment key={item.name}>
            <Styled.BodyCell
              showCursor={item?.options?.length === 0}
              onClick={() => toggleExpand(item.name)}
            >
              <Styled.BodyCellContent>
                <div>{item.name}</div>
                {item.softwareType && (
                  <Styled.SubOptions>
                    <strong>{i18n['Software Type']}</strong> {item.softwareType}
                  </Styled.SubOptions>
                )}
                {item.serviceTerm && !isServiceTermHideRequired && (
                  <Styled.SubOptions>
                    <strong>{i18n['Service Term']}</strong>: {item.serviceTerm}
                  </Styled.SubOptions>
                )}
              </Styled.BodyCellContent>
            </Styled.BodyCell>
            <Styled.BodyCell
              showCursor={item?.options?.length === 0}
              onClick={() => toggleExpand(item.name)}
            >
              {item.quantity}
            </Styled.BodyCell>
            <Styled.BodyCell
              showCursor={item?.options?.length === 0}
              onClick={() => toggleExpand(item.name)}
            >
              {item.displayItemUnitPrice}
            </Styled.BodyCell>
            <Styled.BodyCell
              showCursor={item?.options?.length === 0}
              onClick={() => toggleExpand(item.name)}
            >
              {item.promotion?.amount}
              <br />
              {item.promotion?.programName}
              {item.promotion?.programCode &&
                `(${item.promotion?.programCode})`}
            </Styled.BodyCell>
            <Styled.BodyCell
              showCursor={item?.options?.length === 0}
              onClick={() => toggleExpand(item.name)}
            >
              {item.displayItemTotal}
              {item.promotion?.amount && (
                <Styled.Discount>
                  <br />( {item.promotion?.amount} Off )
                </Styled.Discount>
              )}
              <Styled.CloseIconContainer>
                {item?.options?.length > 0 && (
                  <motion.div
                    animate={{
                      rotate: expandedItems[item.name] ? 180 : 0,
                    }}
                    transition={{ duration: 0.1 }}
                  >
                    <MdKeyboardArrowDown size={30} />
                  </motion.div>
                )}
              </Styled.CloseIconContainer>
            </Styled.BodyCell>
            <AnimatePresence>
              {expandedItems[item.name] &&
                item.options?.map(option => (
                  <React.Fragment key={option.name}>
                    <Styled.BodyCellOptions {...BodyCellOptionsAnimationProps}>
                      <Styled.BodyCellContent>
                      {i18n[option.name] || option.name}
                      </Styled.BodyCellContent>
                    </Styled.BodyCellOptions>
                    <Styled.BodyCellOptions {...BodyCellOptionsAnimationProps}>
                      {option.quantity}
                    </Styled.BodyCellOptions>
                    <Styled.BodyCellOptions {...BodyCellOptionsAnimationProps}>
                      {option.displayItemUnitPrice}
                    </Styled.BodyCellOptions>
                    <Styled.BodyCellOptions
                      {...BodyCellOptionsAnimationProps}
                    ></Styled.BodyCellOptions>
                    <Styled.BodyCellOptions {...BodyCellOptionsAnimationProps}>
                      {option.displayItemTotal}
                    </Styled.BodyCellOptions>
                  </React.Fragment>
                ))}
            </AnimatePresence>
          </React.Fragment>
        ))}
      </Styled.BodyRows>
    </Styled.Proposal>
  );
};

export default StandardOrderDetails;
